import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Row, Col, Button, Table } from 'react-bootstrap';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { omit as _omit } from 'lodash';
import moment from 'moment';
import 'moment-timezone';

import { changeUploadContext, resetUploadContext } from 'actions/UploadActions';
import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';
import { addNotification } from 'actions/NotificationsActions';

import EntityStateUpdater from 'component/EntityStateUpdater';

import Entity from 'components/Entity';
import List from 'components/List';
import Data from 'components/Data';

import FormErrors from 'components/forms/FormErrors';

import Loader from 'components/layout/Loader';
import Breadcrumb from 'components/layout/Breadcrumb';
import SimplePrompt from 'components/layout/SimplePrompt';

import ClientLocationDetails from 'components/layout/client/ClientLocationDetails';

import { entityMetaMapping, ROLE_ADMIN } from 'js/constants';
import may from 'js/security';

import DocMeta from 'components/DocMeta';
import { generateMetaTags } from 'helpers/metaTags';

class Locations extends EntityStateUpdater {
  constructor(props) {
    super(props);

    moment.locale(globalLocale);

    this.state = {
      entityLoaded: false,
      shownIndex: null,
    };

    this.entity = new Entity();

    if (props.meta.LocationList !== undefined && props.meta[entityMetaMapping.clientLocation] !== undefined) {
      this.entity.setMeta(props.meta, 'LocationList');
    }
  }

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      meta,
      data,
      formStatus,
      doNotify,
    } = this.props;
    const { entity, entityLoaded } = this.state;

    if (prevProps.data.pending && !data.pending) {
      this.entity.setData({ locations: data.data.list });
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (
      meta[entityMetaMapping.clientLocation] !== undefined &&
      !meta[entityMetaMapping.clientLocation].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, 'LocationList');
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError) {
      doNotify(1, messages.locations_saved);
    }

    if (entityLoaded && prevProps.data.data.list && data.data.list) {
      // image has been uploaded, update the location imgage list
      for (let i = 0; i < data.data.list.length; i++) {
        if (
          data.data.list[i].imgs &&
          entity.getLocations()[i] &&
          data.data.list[i].imgs.length !== entity.getLocations()[i].getImgs().length
        ) {
          // we found the location where count of images has changed
          const imgs = [];
          for (let j = 0; j < data.data.list[i].imgs.length; j++) {
            imgs.push(this.state.entity.createValue('imgs', ['locations', i.toString()], data.data.list[i].imgs[j]));
          }
          this.updateEntity('imgs', imgs, ['locations', i.toString()]);

          // end the loop
          break;
        }
      }
    }
  };

  handleClick = index => {
    this.setState(prevState => ({ shownIndex: index === prevState.shownIndex ? null : index }));

    if (false) {
    } else {
      this.props.updateUploadContext({
        type: 'companyLocation_img',
        locationId: this.state.entity.getLocations()[index].getId(),
        uploadLink: '/api/locations/' + this.state.entity.getLocations()[index].getId() + '/assets.json',
      });
    }
  };

  componentWillUnmount = () => {
    this.props.doResetUploadContext();
  };

  closeDetails = () => {
    this.setState({ shownIndex: null });
  };

  addLocation = () => {
    let { entity } = this.state;
    let newLocation = entity.createValue('locations', []);
    let newValue = entity.getLocations().slice();
    newValue.push(newLocation);
    let newEntity = { ...entity.updateValue('locations', newValue, []) };

    this.setState({ shownIndex: newValue.length - 1 });

    this.changeEntity(newEntity);
  };

  handleRemoveClick = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    this.removeEntry(index);
  };

  removeEntry = index => {
    let { entity } = this.state;
    let newValue = entity.getLocations().slice();
    newValue.splice(index, 1);
    let newEntity = { ...entity.updateValue('locations', newValue, []) };

    if (index < this.state.shownIndex) {
      this.setState(
        prevState => ({ shownIndex: prevState.shownIndex - 1 }),
        () => {
          this.changeEntity(newEntity);
        }
      );
    } else if (index === this.state.shownIndex) {
      this.setState({ shownIndex: null }, () => {
        this.changeEntity(newEntity);
      });
      this.changeEntity(newEntity);
    } else {
      this.changeEntity(newEntity);
    }
  };

  duplicateLocation = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    const { entity } = this.state;

    let data = entity.copyObject(entity.getLocations()[index]);
    data = _omit(data, ['imgs']);

    const newLocation = entity.createValue('locations', [], data);
    const newValue = entity.getLocations().slice();
    newValue.splice(index + 1, 0, newLocation);
    const newEntity = { ...entity.updateValue('locations', newValue, []) };

    this.changeEntity(newEntity, () => {
      this.setState({ shownIndex: index + 1 });
    });
  };

  formSubmit = e => {
    e.preventDefault();

    this.changeEntity(this.state.entity.validate(), () => {
      if (this.state.entity.isValid) {
        this.props.onSubmit({ locations: this.state.entity.getLocations() });
      }
    });
  };

  render() {
    const {
      intl: { messages },
      formStatus,
      data,
      user,
    } = this.props;
    const { shownIndex, entityLoaded, entity } = this.state;

    const disabled = formStatus.pending;

    const columns = [messages.name, messages.address, messages.zip_code, messages.city, messages.actions];
    let tBody = [];
    if (entityLoaded && entity && entity.getLocations().length) {
      const locations = entity.getLocations();
      tBody = locations.map((location, i) => {
        return (
          <tr
            key={i}
            class={(i === shownIndex ? 'shown ' : '') + (i % 2 ? 'even ' : 'odd ')}
            onClick={() => {
              this.handleClick(i);
            }}
          >
            <td class="v-align-middle semi-bold">{location.getName()}</td>
            <td class="v-align-middle">{location.getStreet()}</td>
            <td class="v-align-middle">{location.getZipCode()}</td>
            <td class="v-align-middle">{location.getCity()}</td>
            <td class="v-align-middle text-right">
              {i === shownIndex ? (
                <Button bsSize="sm" class="btn-rounded m-r-10">
                  <i class="fa fa-times" /> {messages.close}
                </Button>
              ) : (
                <Button bsSize="sm" class="btn-rounded m-r-10">
                  <i class="fa fa-pencil-alt" /> {messages.edit}
                </Button>
              )}
              <Button
                bsSize="sm"
                class="btn-rounded m-r-10"
                onClick={e => {
                  this.duplicateLocation(e, i);
                }}
              >
                <i class="fa fa-files-o" /> {messages.duplicate}
              </Button>
              {may(ROLE_ADMIN, user.roles) && (
                <SimplePrompt
                  onSuccess={e => {
                    this.handleRemoveClick(e, i);
                  }}
                />
              )}
            </td>
          </tr>
        );
      });
    }

    if (shownIndex !== null) {
      // user wants to see a location form
      tBody.splice(
        shownIndex + 1,
        0,
        <ClientLocationDetails
          key={'details-showing-' + shownIndex}
          entityLoaded={entityLoaded}
          entity={entity}
          changeEntity={this.changeEntity}
          disabled={disabled}
          user={user}
          data={entity.getLocations()[shownIndex]}
          colSpan={columns.length}
          shownIndex={shownIndex}
          timezone={user.profile.timezone}
        />
      );
    }

    return (
      <DocMeta tags={generateMetaTags(messages.meta.locations)}>
        <div class="padding-25 sm-padding-10">
          <FormErrors formStatus={formStatus} />

          <form method="put" onSubmit={this.formSubmit}>
            <Breadcrumb
              crumbs={[
                { link: this.props.appIntl.urlPrefix + 'account', name: 'Dashboard' },
                { link: this.props.appIntl.urlPrefix + 'account/settings', name: messages.settings },
                { link: null, name: messages.locations },
              ]}
              className="p-t-0"
            />

            <Data dataName="clientLocations" />
            <Data dataName="clientLocation" />
            <List
              noRender
              listName="companyLocations"
              url="/api/locations.json"
              list={data}
              urlData={{ filters: { parent: true } }}
            />

            {entityLoaded ? (
              <Row>
                <Col xs={12}>
                  <div class="text-right m-b-10">
                    <Button
                      bsSize="lg"
                      disabled={formStatus.pending}
                      type="submit"
                      bsStyle={this.state.entityChanged ? 'primary' : 'default'}
                    >
                      <i class={'fa-fw ' + (formStatus.pending ? ' fa fa-circle-notch fa-spin' : 'fal fa-save')} />{' '}
                      {messages.save}
                    </Button>
                  </div>
                </Col>

                <Col xs={12}>
                  <Table responsive hover class="table-detailed">
                    <thead>
                      <tr>
                        {columns.map((column, i) => {
                          return (
                            <th key={i} class={column === messages.actions ? 'text-right' : ''}>
                              {column}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <ReactCSSTransitionGroup
                      transitionName="tablerows"
                      transitionEnterTimeout={500}
                      transitionLeaveTimeout={1}
                      component="tbody"
                    >
                      {tBody}
                    </ReactCSSTransitionGroup>
                  </Table>

                  <div class="m-t-10">
                    <Button onClick={this.addLocation}>{messages.add_location}</Button>
                  </div>
                </Col>
              </Row>
            ) : (
              <Loader />
            )}
          </form>
        </div>
      </DocMeta>
    );
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    meta: state.entityMeta,
    data: state.list.companyLocations,
    formStatus: state.formStatus.companyLocations,
    user: state.login.user,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    updateUploadContext: newContext => {
      dispatch(changeUploadContext(newContext));
    },
    doResetUploadContext: () => {
      dispatch(resetUploadContext());
    },
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    onSubmit: args => {
      formStatusDispatchHelper('companyLocations', '/api/locations.json', args, 'put', dispatch);
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Locations)
);
